import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { daysAgo } from "./highlight-card";
import { HIGHLIGHT_CONFIG, HighlightMaterials } from "./highlights";
import cn from "clsx";

type HighlightPreviewProps = {
  highlight: HighlightMaterials;
  isSelected?: boolean;
  onSelect?: () => void;
  className?: string;
};

export function RecentHighlightEntry({
  highlight,
  isSelected,
  onSelect,
  className,
}: HighlightPreviewProps) {
  const config = HIGHLIGHT_CONFIG.find((c) => c.id === highlight.type);
  if (!config) return null;

  const Icon = config.icon;
  const sourceCount = highlight.metadata?.document_ids?.length ?? 0;

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isSelected}
      className={cn(
        "group w-full text-left",
        "flex items-center gap-3 rounded-lg px-3 py-2 group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:py-2 group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:justify-center",
        "transition focus:outline-none",
        config.color.hoverBg,
        isSelected && config.color.bg,
        className
      )}
    >
      {/* Icon */}

      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn(
                "flex h-7 w-7 items-center justify-center shrink-0",
                config.color.text,
                isSelected && "scale-105"
              )}
            >
              <Icon className="h-5 w-5" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            <span className="text-xs">{highlight.name}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Content */}
      <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
        {/* Title */}
        <div
          className={cn(
            "truncate text-sm font-semibold transition-colors",
            isSelected ? config.color.text : "text-theme-0"
          )}
        >
          {highlight.name || "Untitled highlight"}
        </div>

        {/* Meta row */}
        <div
          className={cn(
            "mt-0.5 flex items-center gap-2 text-[11px] text-theme-1"
          )}
        >
          <span>
            {sourceCount} source{sourceCount === 1 ? "" : "s"}
          </span>
          <span>•</span>
          <span>{daysAgo(highlight.created_at)}</span>
        </div>
      </div>
    </button>
  );
}
